import { LegalEntityCountry } from '@seapoint/server/enums';
import { FormSelectField } from '../../../../../../design-system/form-select-field/form-select-field';
import { FormTextField } from '../../../../../../design-system/form-text-field/form-text-field';
import { Inline } from '../../../../../../design-system/inline/inline';
import { Select } from '../../../../../../design-system/select/select';
import { Stack } from '../../../../../../design-system/stack/stack';
import { supportedCountryCurrencies } from '../../../../../../types/supported-country-currency';
import { getCountryFlag } from '../../../../../../utils/get-country-flag';
import { getCurrencyDetails } from '../../../../../../utils/get-currency-details';

const COUNTRY_DISPLAY_NAMES = {
  GB: 'United Kingdom',
  IE: 'Ireland',
  DE: 'Germany',
} as const;

const COUNTRY_CURRENCY_MAP = {
  GB: 'GBP',
  IE: 'EUR',
  DE: 'EUR',
} as const;

type BankInformationProps = {
  legalEntityCountry: LegalEntityCountry;
};

export const BankInformation = ({ legalEntityCountry }: BankInformationProps) => {
  const getCountryName = (countryCode: LegalEntityCountry) => {
    return COUNTRY_DISPLAY_NAMES[countryCode];
  };

  const availableCurrencies = supportedCountryCurrencies.filter((currency) => {
    const countryCurrency = COUNTRY_CURRENCY_MAP[legalEntityCountry];
    return currency === countryCurrency;
  });

  const isIBAN = legalEntityCountry !== LegalEntityCountry.GB;

  return (
    <Stack space={6} className="w-full">
      <Stack space={4} className="w-full">
        <h2 className="text-lg font-semibold text-muted-foreground">Bank Information</h2>

        <FormSelectField label="Country" inputName="country" placeholder="Select country" triggerClassName="text-base">
          <Select.Item key={legalEntityCountry} value={legalEntityCountry} className="py-3">
            <Inline space={3}>
              <img
                src={getCountryFlag(legalEntityCountry)}
                alt={getCountryName(legalEntityCountry)}
                className="h-6 w-6 rounded-full object-cover"
              />
              <p className="text-base font-medium text-black">{getCountryName(legalEntityCountry)}</p>
            </Inline>
          </Select.Item>
        </FormSelectField>

        <FormSelectField
          label="Currency"
          inputName="currency"
          placeholder="Select currency"
          triggerClassName="text-base"
        >
          {availableCurrencies.map((currencyCode) => {
            const { flag, name } = getCurrencyDetails(currencyCode);
            const currencyLabel = `${name} (${currencyCode})`;

            return (
              <Select.Item key={currencyCode} value={currencyCode} className="py-3">
                <Inline space={3}>
                  <div className="h-6 w-6 overflow-clip rounded-full">
                    <img src={flag} alt="" loading="eager" fetchpriority="high" className="h-full w-full" />
                  </div>
                  <p className="text-base font-medium text-black">{currencyLabel}</p>
                </Inline>
              </Select.Item>
            );
          })}
        </FormSelectField>

        {isIBAN ? (
          <div className="w-full">
            <FormTextField label="IBAN" inputName="iban" placeholder="Enter IBAN" />
          </div>
        ) : (
          <Inline space={4} className="w-full justify-between" wrap="nowrap">
            <div className="w-1/2">
              <FormTextField label="Account Number" inputName="accountNumber" placeholder="Enter account number" />
            </div>
            <div className="w-1/2">
              <FormTextField label="Sort Code" inputName="sortCode" placeholder="Enter sort code" />
            </div>
          </Inline>
        )}
      </Stack>
    </Stack>
  );
};
